import React from 'react';
import moment from 'moment-timezone';
import { Drawer } from 'antd';
import { Server } from '../types/order';
import {
  DeliveryProvider,
  OrderFulfillmentStatus,
  OrderMethod,
  OrderPaymentStatus,
  PaymentMethod,
} from '../types/constants';
import AddressFormatter from '../services/utils/address/AddressFormatter';
import {
  ClockCircleOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  TruckOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { BaseComponentProps, WithClassname } from '../types';
import classnames from 'classnames';
import useHelpers from '../hooks/utility/useHelpers';
import DeliveryProviderIcon from '../assets/icons/DeliveryProviderIcon';
import { Client as ClientBasket } from '../types/basket';
import { collect } from 'collect.js';

type Props = {
  data: Server.OrderData;
  onClose: () => void;
};

const Divider = ({ children, className }: BaseComponentProps & WithClassname) => (
  <div
    className={classnames(
      'bg-gray-500 w-[120%] -ml-[10%] pl-[10%] py-sm font-semibold ',
      className,
    )}
  >
    <h6>{children}</h6>
  </div>
);

type OrderLineItemProps = WithClassname & {
  data: ClientBasket.OrderLineItem & { id: number };
};

const OrderLineItem = ({ data, className }: OrderLineItemProps) => {
  const { formatCurrency } = useHelpers();

  const freeModifiers = collect(data.modifiers).filter((item) => item.package_price === 0);
  const paidModifiers = collect(data.modifiers).filter((item) => item.package_price !== 0);

  return (
    <div className={classnames('py-sm', className)}>
      <div className="grid grid-cols-12">
        <p className="font-semibold">{data.quantity}x</p>
        <p className="font-semibold col-span-9 break-words">{data.product.name}</p>
        <p className="col-span-2 text-right">{formatCurrency(data.item_price)}</p>
      </div>
      {freeModifiers.isNotEmpty() && (
        <div className="grid grid-cols-12 mt-xs">
          <div />
          <p className="col-span-11 text-sm">{freeModifiers.implode('modifier.name', ' · ')}</p>
        </div>
      )}
      {paidModifiers.map((item, index) => (
        <div className="grid grid-cols-12 mt-xs" key={`line-item-${data.id}-modifier-${index}`}>
          <div />
          <p className="col-span-9 text-sm">+{item.modifier.name}</p>
          <p className="col-span-2 text-right">{formatCurrency(item.package_price)}</p>
        </div>
      ))}
      {Boolean(data.note) && (
        <div className="grid grid-cols-12 mt-xs">
          <div />
          <p className="col-span-11 text-sm italic">{data.note}</p>
        </div>
      )}
    </div>
  );
};

const PaymentItem = ({
  name,
  amount,
  className,
}: WithClassname & { name: string; amount: number }) => {
  const { formatCurrency } = useHelpers();

  return (
    <div className={classnames('mt-sm flex justify-between', className)}>
      <p>{name}</p>
      <p>{formatCurrency(amount)}</p>
    </div>
  );
};

export default function OrderDetailsModal({ data, onClose }: Props) {
  const { formatCurrency, formatTime, formatDateTime } = useHelpers();

  const renderOrderMethodIcon = () => {
    if (
      data.method === OrderMethod.Delivery &&
      data.delivery_provider !== DeliveryProvider.Standalone
    ) {
      return <DeliveryProviderIcon provider={data.delivery_provider} className="h-12 w-12" />;
    }

    const icon =
      {
        [OrderMethod.Pickup]: <ShoppingOutlined className="text-lg m-auto" />,
        [OrderMethod.Delivery]: <TruckOutlined className="text-lg m-auto" />,
      }[data.method] ?? null;

    return Boolean(icon) && <div className="h-14 w-14 flex bg-gray-400 rounded-full">{icon}</div>;
  };

  return (
    <Drawer
      open
      width={window.innerWidth * 0.4}
      onClose={onClose}
      title="Order details"
      footer={<p className="text-center">{data.fulfillment?.status ?? 'Awaiting payment'}</p>}
    >
      <div className="flex justify-between">
        <div>
          <h5>#{data.fulfillment?.number ?? data.id}</h5>
          <p className="font-semibold text-sm">
            {data.customer_first_name} {data.customer_last_name}
          </p>
          {data.method === OrderMethod.Delivery && (
            <>
              <p>
                {AddressFormatter.setCountry(data.delivery_country).format({
                  street_name: data.delivery_street_name,
                  street_number: data.delivery_street_number,
                  city: data.delivery_city,
                  zip_code: data.delivery_zip_code,
                })}
              </p>
              {Boolean(data.delivery_doorbell_name) && <p>{data.delivery_doorbell_name}</p>}
              {Boolean(data.delivery_company_name) && <p>{data.delivery_company_name}</p>}
            </>
          )}
          <p>
            <span className="font-semibold">Phone:</span> {data.customer_phone_number}
          </p>
          {Boolean(data.customer_email) && (
            <p>
              <span className="font-semibold">Email:</span> {data.customer_email}
            </p>
          )}
        </div>
        <div>
          <div className="flex justify-center">{renderOrderMethodIcon()}</div>
          <p className="text-center">{data.method}</p>
          {data.payment_status !== OrderPaymentStatus.Pending && (
            <div className="bg-black text-white text-center">
              {data.scheduled_for ? formatTime(data.fulfillment.estimated_pickup_time) : 'ASAP'}
            </div>
          )}
          {/*<DeliveryTimeDifferenceBadge*/}
          {/*  initial={data.delivery?.initial_pickup_estimate}*/}
          {/*  actual={data.delivery?.pickup_estimate}*/}
          {/*  className="w-full mt-2"*/}
          {/*/>*/}
        </div>
      </div>
      {data.payment_status !== OrderPaymentStatus.Pending &&
        data.scheduled_for &&
        data.fulfillment.status !== OrderFulfillmentStatus.Preparing && (
          <div className="w-full bg-blue-500 text-white">
            <h5 className="text-center">
              Scheduled for{' '}
              {moment(data.fulfillment.estimated_pickup_time).isSame(moment(), 'day')
                ? 'today'
                : formatDateTime(data.fulfillment.estimated_pickup_time, {
                    format: 'dddd, DD MMM',
                  })}
            </h5>
          </div>
        )}
      {Boolean(data.delivery?.runner_name) && (
        <div>
          <Divider className="my-sm">Driver info</Divider>
          <p>
            <span className="font-semibold">Driver name</span>
            {': '}
            {data.delivery.runner_name}
          </p>
          {Boolean(data.delivery?.runner_phone_number) && (
            <p>
              <span className="font-semibold">Driver phone number</span>
              {': '}
              {data?.delivery?.runner_phone_number}
            </p>
          )}
        </div>
      )}
      <Divider className="mt-sm">Order info</Divider>
      {data.line_items.map((item, index) => (
        <OrderLineItem
          data={item}
          className={classnames({ 'border-t': index > 0 })}
          key={`line-item-${index}`}
        />
      ))}
      <Divider>Price info</Divider>
      <PaymentItem name="Original" amount={data.sub_total} />
      {data.method === OrderMethod.Delivery && data.method_fee > 0 && (
        <PaymentItem name="Delivery fee" amount={data.method_fee} />
      )}
      {data.tip > 0 && <PaymentItem name="Tip" amount={data.tip} />}
      {data.service_fee > 0 && <PaymentItem name="Online processing" amount={data.service_fee} />}
      {data.payment_method_upcharge_fee > 0 && (
        <PaymentItem name="PayPal fee" amount={data.payment_method_upcharge_fee} />
      )}
      {data.total_discount > 0 && <PaymentItem name="Discount" amount={-data.total_discount} />}
      {data.additional_tax > 0 && <PaymentItem name="Taxes" amount={data.additional_tax} />}
      <div className="mt-xs flex justify-between">
        <p className="font-semibold">Total</p>
        <p className="font-semibold">{formatCurrency(data.total)}</p>
      </div>
      <p className="mt-sm">
        {data.payment_status === OrderPaymentStatus.Pending ? (
          <>
            <ClockCircleOutlined className="mr-xs text-md" />
            Awaiting payment
          </>
        ) : (
          <>
            <span className="mr-xs text-md">
              {data.payment.payment_method_id === PaymentMethod.Cash ? (
                <WalletOutlined />
              ) : (
                <CreditCardOutlined />
              )}
            </span>
            {data.payment.payment_method_id === PaymentMethod.Cash
              ? 'Payment in cash'
              : 'Customer paid online'}
          </>
        )}
      </p>
      {data.payment?.adjustments.length > 0 && (
        <>
          <Divider className="mt-sm">Adjustments</Divider>
          {data.payment.adjustments.map((item) => (
            <div className="mt-sm" key={`adjustment-${item.id}`}>
              <PaymentItem
                name={formatDateTime(item.paid_at)}
                amount={item.total}
                className="font-semibold"
              />
              {Boolean(item.adjustment_reason) && (
                <p className="underline">{item.adjustment_reason}</p>
              )}
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}
